import { Trans, t } from '@lingui/macro';
import { Info, X, Edit, Loader2 } from "lucide-react";
import { useState } from "react";
import { BotMessage, ChunkInfo } from "../../models/BotMessage";
import { Soribot } from "../../models/Soribot";
import { SourceDocument } from "../../models/SoribotSource";
import { Fire } from "../../services/FireClient";
import { UtilApp } from "../../services/Utils/UtilApp";
import SourceDocEditor from "./SourceDocEditor";
import { DataSourceIcon } from "./compontents/Icons";
import { SuggestionFormModal } from "./compontents/SuggestionFormModal";
import { My } from "../../services/My";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { ScrollArea } from "../../components/ui/scroll-area";

export const BotMessageInfoModal: React.FC<{ botMessageId: string | null, bmsg: BotMessage | null }> = ({ bmsg, botMessageId }) => {
  const [show, setShow] = useState(false);
  const [bm, setBotMsg] = useState<BotMessage>();
  const [inProgress, setInProgress] = useState(false);

  async function onPresent() {
    if (bmsg) {
      setBotMsg(bmsg);
    } else if (botMessageId) {
      setInProgress(true);
      setBotMsg(await Fire.bot.getBotMessage(botMessageId));
      setInProgress(false);
    }
  }

  const handleOpenChange = (open: boolean) => {
    setShow(open);
    if (open) {
      onPresent();
    }
  };

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Info className={`w-4 h-4 cursor-pointer text-gray-400 hover:text-gray-600 ${bmsg?.llmPrompt ? 'fill-current' : ''}`} />
      </DialogTrigger>
      <DialogContent className="max-w-[800px] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle><Trans>Prompt information</Trans></DialogTitle>
        </DialogHeader>
        {inProgress && <Progress value={undefined} className="h-1 w-full" />}

        {bm && <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            {bm.errorMessage && <div className="text-red-500 bg-red-50 p-2 rounded">{bm.errorMessage}</div>}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {TextItem(t`Bot name`, bm.soribotName)}
              {TextItem(t`Type`, bm.subType)}
              {TextItem(t`Temperature`, bm.llmTemperature?.toString())}
              {TextItem(t`Date`, `${UtilApp.dateStrToTimeAndDay(bm.started, My.user.lang)}`)}
              {TextItem(t`KB Search time`, bm.timeVectorDBCall ? `${bm.timeVectorDBCall.toString()} ms` : t`N/A`)}
              {TextItem(t`LLM time`, bm.timeLLMCall ? `${bm.timeLLMCall.toString()} ms` : t`N/A`)}
            </div>

            <div className="border rounded p-4 space-y-4">
              <div className="grid md:grid-cols-[100px_1fr] gap-4">
                <div className="font-medium text-gray-500">
                  <Trans>User Prompt</Trans>
                  {bm.suggestionId && <p className="text-xs text-gray-400">(<Trans>Suggestion</Trans>)</p>}
                </div>
                <div className="whitespace-pre-wrap bg-gray-50 p-2 rounded border">{bm.userPrompt}</div>
              </div>

              <hr className="border-gray-100" />

              <div className="grid md:grid-cols-[100px_1fr] gap-4">
                <div className="font-medium text-gray-500">
                  <Trans>Bot Response</Trans>
                  {bm.suggestionId && !bm.llmPrompt && <p className="text-xs text-gray-400">(<Trans>Fixed answer</Trans>)</p>}
                </div>
                <div className="whitespace-pre-wrap bg-gray-50 p-2 rounded border">{bm.llmResponse}</div>
              </div>
            </div>

            <SuggestionFormModal bm={bm} />

            <hr className="border-gray-200" />

            {bm.chunkInfos && (
              <div className="space-y-2">
                <h3 className="font-medium text-gray-700"><Trans>Data sources</Trans></h3>
                {bm.chunkInfos.map((chunk, i) => (
                  <div key={i} className="border rounded p-3 text-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 font-medium">
                          {DataSourceIcon(chunk.type)} {chunk.title} - <Trans>Chunk</Trans> {chunk.chunkNr}
                        </div>
                        <small className="text-gray-400 block">
                          {chunk.distance && <span title="The lower the closer to the original question. Spans between 0 - 1.">Distance: {chunk.distance.toFixed(3)}</span>}
                        </small>
                      </div>
                      <OpenSourceDocButton soribotId={bm.soribotId} ci={chunk} />
                    </div>
                    <div className="text-gray-600 bg-gray-50 p-2 rounded">{chunk.chunkText.substring(0, 200)}{chunk.chunkText.length > 200 && '...'}</div>
                  </div>
                ))}
              </div>
            )}

            {bm.llmPrompt && (
              <Accordion type="single" collapsible>
                <AccordionItem value="full-prompt">
                  <AccordionTrigger className="bg-gray-100 px-4 rounded hover:no-underline"><Trans>View Full Prompt</Trans></AccordionTrigger>
                  <AccordionContent className="p-4 space-y-2">
                    <p className="text-sm"><span className="font-bold"><Trans>Sent to OpenAI</Trans></span> {bm.llmModelName}</p>

                    <p className="text-sm font-bold mt-2"><Trans>System instruction</Trans></p>
                    <Textarea readOnly value={bm.systemInstruction ?? t`None`} className="font-mono text-xs bg-gray-50 min-h-[100px]" />

                    <p className="text-sm font-bold mt-2"><Trans>Message</Trans></p>
                    <Textarea readOnly value={bm.llmPrompt ?? t`None`} className="font-mono text-xs bg-gray-50 min-h-[200px]" />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>
        </ScrollArea>}
      </DialogContent>
    </Dialog>
  );
}

function TextItem(label: string | undefined, value?: any) {
  return <div className="flex flex-col"><span className="font-bold text-gray-500 text-xs">{label}:</span> <span className="truncate">{value}</span></div>;
}

// ====================================================================================================

const OpenSourceDocButton: React.FC<{ soribotId: string, ci: ChunkInfo }> = ({ soribotId, ci }) => {
  if (ci.type !== 'source-doc') {
    return null;
  }
  const [openedDoc, setOpenedDoc] = useState<SourceDocument | null>(null);
  const [bot, setBot] = useState<Soribot | null>(null);
  const [inProgress, setInprogress] = useState(false);
  const { toast } = useToast();

  return <>
    <div className="flex items-center">
      {!inProgress && <Button variant="outline" size="sm" onClick={openDoc}><Trans>Edit</Trans></Button>}
      {inProgress && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
    </div>
    {openedDoc && bot && <SourceDocEditor bot={bot} sd={openedDoc} onClose={() => setOpenedDoc(null)} chunkNr={ci.chunkNr} />}
  </>

  async function openDoc() {
    try {
      setInprogress(true);
      const sd = await Fire.bot.getSourceObj(soribotId, ci.sourceId);
      setOpenedDoc(sd as SourceDocument);
      setBot(await Fire.bot.getSoribot(soribotId));
    } catch (error: any) {
      toast({ description: error.message, variant: "destructive" });
    } finally {
      setInprogress(false);
    }
  }
}

export default BotMessageInfoModal;
